import { useQueryState } from '@dustin-web/microsite-components/src/hooks'
import { smoosh } from '@dustin-web/microsite-components/src/utils/array-helpers'
import ErrorBoundary from '@dustin-web/microsite-components/src/utils/error-boundary'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import React, { useCallback, useMemo, useState } from 'react'
import { DebounceInput } from 'react-debounce-input'
import { FacetGroup } from '.'

const Nouislider = dynamic(() => import('@dustin-web/microsite-components/src/slider'))

type Props = {
  facetGroup: FacetGroup
  className?: string
}

type SliderRangeValues = {
  [key: string]: number | number[]
}

const logScale = (hits: number, maxHits: number) => {
  if (hits <= 0) {
    return 0
  }

  const logValue = (100 * Math.log(hits)) / Math.log(maxHits)
  const value = isNaN(logValue) ? 100 : logValue

  return logValue <= 0 ? 1 : value
}

const countDecimals = (val: number) => {
  if (Math.floor(val) === val) {
    return 0
  }

  return val.toString().split('.')[1].length || 0
}

const buildFacetData = (
  facetGroup: FacetGroup,
  getActiveFilterValue: (key: string) => string[]
) => {
  const groupValues = [...facetGroup.values]
  const values = groupValues.sort((a, b) => (a.min || 0) - (b.min || 0))

  const initialMax = Math.max(...values.map(v => v.max || 0))
  const initialMin = Math.min(...values.map(v => v.min || 0))
  const significance = Math.min(Math.max(countDecimals(initialMin), countDecimals(initialMax)), 2)

  const valuesStateFromUrl = getActiveFilterValue(facetGroup.nameInUrl)
  const selectedValues = valuesStateFromUrl
    ? smoosh(valuesStateFromUrl.map(v => v.split('-'), []))
    : []

  const selectedValuesSorted = selectedValues.sort((a, b) => a - b)

  const selectedMinNumber = Number(selectedValuesSorted[0] || initialMin)
  const selectedMaxNumber = Number(
    selectedValuesSorted[selectedValuesSorted.length - 1] || initialMax
  )

  return {
    initialMin,
    initialMax,
    values,
    significance,
    selectedMinNumber,
    selectedMaxNumber,
  }
}

export const FacetGroupRange = ({ facetGroup, className }: Props) => {
  const { setQueryState, getActiveFilterValue } = useQueryState()
  const [invalidMinValue, setInvalidMinValue] = useState(false)
  const [invalidMaxValue, setInvalidMaxValue] = useState(false)

  const {
    initialMin,
    initialMax,
    values,
    significance,
    selectedMaxNumber,
    selectedMinNumber,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  } = useMemo(() => buildFacetData(facetGroup, getActiveFilterValue), [facetGroup])

  const [sliderText, setSliderText] = useState({
    maxValueText: selectedMaxNumber.toFixed(significance),
    minValueText: selectedMinNumber.toFixed(significance),
  })

  const [minRangeValue, setMinRangeValue] = useState(selectedMinNumber)
  const [maxRangeValue, setMaxRangeValue] = useState(selectedMaxNumber)

  const facetGroupMaxValue = Math.max(
    ...values.map(x => x.max).filter((val): val is number => !!val)
  )

  const facetGroupMinValue = Math.min(
    ...values.map(x => x.min).filter((val): val is number => !!val)
  )

  const maxHits = Math.max(...values.map(v => v.hits))

  const onSliderChange = useCallback(
    (stopped: boolean) => (_render: any, _handle: any, value: number[]) => {
      const [minVal, maxVal] = value.map(val => {
        const rounded = Math.round(val * 100) / 100
        return rounded
      })

      if (stopped && minVal <= facetGroupMinValue && maxVal === facetGroupMaxValue) {
        setQueryState(
          {
            [facetGroup.nameInUrl]: '',
          },
          false
        )
        return
      }

      if (stopped) {
        setQueryState(
          {
            [facetGroup.nameInUrl]: `${minVal}-${maxVal}`,
            page: '',
          },
          false
        )
      } else {
        setSliderText({
          maxValueText: maxVal.toFixed(significance),
          minValueText: minVal.toFixed(significance),
        })
      }
    },
    [facetGroup, facetGroupMaxValue, facetGroupMinValue, setQueryState, significance]
  )

  const onInputChange = useCallback(
    (minValue: number, maxValue: number, origin: 'min' | 'max') => {
      if (!minValue || !maxValue) {
        return
      }

      setMinRangeValue(minValue)
      setMaxRangeValue(maxValue)

      setInvalidMinValue(false)
      setInvalidMaxValue(false)

      if (minValue > maxValue) {
        setInvalidMinValue(origin === 'max')
        setInvalidMaxValue(origin === 'min')
        return
      }

      setQueryState(
        {
          [facetGroup.nameInUrl]: `${minValue}-${maxValue}`,
          page: '',
        },
        false
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [facetGroup.nameInUrl]
  )

  const sliderRange = useMemo(
    () =>
      values.reduce(
        (acc, val, i) => {
          const rangePercentage = Math.floor(((i + 1) / values.length) * 100)
          acc[`${rangePercentage}%`] = val.max || 0
          return acc
        },
        { min: initialMin, max: initialMax } as SliderRangeValues
      ),
    [initialMin, initialMax, values]
  )

  const debounceInputDefaults = {
    className: 'c-filter-slider-input',
    debounceTimeout: 900,
    inputMode: 'numeric',
    minLength: 1,
    type: 'text',
  }

  return (
    <div
      className={classNames('c-filter-slider u-pb-16 u-px-16', className, {
        'c-filter-slider--active':
          selectedMinNumber !== initialMin || selectedMaxNumber !== initialMax,
      })}>
      {initialMin !== initialMax && (
        <>
          <div className="c-filter-slider__bars">
            {values.map((range, key) => {
              const hitPercentage = logScale(range.hits, maxHits)
              const isInSelectedRange =
                selectedMinNumber <= (range.max || 0) && selectedMaxNumber >= (range.min || 0)

              return (
                <div
                  key={range.name + key}
                  className={classNames('c-filter-slider__bar', {
                    'c-filter-slider__bar--out-of-range': !isInSelectedRange,
                  })}
                  style={{
                    height: `${(hitPercentage < 30 && hitPercentage > 0 ? 30 : hitPercentage).toFixed(2)}%`,
                    width: `${(100 / facetGroup.values.length).toFixed(2)}%`,
                  }}
                  title={`${range.hits} ${range.hits === 1 ? 'produkt' : 'produkter'}`}
                />
              )
            })}
          </div>

          <ErrorBoundary>
            <Nouislider
              // @ts-ignore
              className="c-filter-slider__base"
              range={sliderRange}
              start={[selectedMinNumber, selectedMaxNumber]}
              onChange={onSliderChange(true)}
              onSlide={onSliderChange(false)}
              connect
            />
          </ErrorBoundary>
        </>
      )}
      <div className={
        classNames('c-form c-form--dark u-relative u-mb-8 u-w-100',
          { 'c-form__error': invalidMinValue }
        )}>
        <label htmlFor={`${facetGroup.name}-min-value`} className="u-mb-4 u-fs-12">
          Min
        </label>
        <DebounceInput
          {...debounceInputDefaults}
          id={`${facetGroup.name}-min-value`}
          value={`${sliderText.minValueText}`}
          onChange={event => onInputChange(Number(event.target.value.trim()), maxRangeValue, 'min')}
        />
        <span className="c-filter-slider__unit">{facetGroup.unitText}</span>
      </div>
      <div className={
        classNames('c-form c-form--dark u-relative u-w-100',
          { 'c-form__error': invalidMaxValue }
        )}>
        <label htmlFor={`${facetGroup.name}-max-value`} className="u-mb-4 u-fs-12">
          Max
        </label>
        <DebounceInput
          {...debounceInputDefaults}
          id={`${facetGroup.name}-max-value`}
          value={`${sliderText.maxValueText}`}
          onChange={event => onInputChange(minRangeValue, Number(event.target.value.trim()), 'max')}
        />
        <span className="c-filter-slider__unit">{facetGroup.unitText}</span>
      </div>
    </div>
  )
}
